export enum ResourceType{
    SMS = 'SMS',
    EMAIL = 'EMAIL',
    AI_TOKENS = 'AI_TOKENS',
    STORAGE = 'STORAGE',
    WORK_UNIT = 'WORK_UNIT'
}

export interface AppResource {
    id: string;
    pricePerResource: number;
    availableResourceCount: number;
    resourceType: ResourceType;
}

export interface AppResourceExtraInfo extends AppResource {
    name: string;
    cover: string;
    resRemainLabel: string;
    isAvailable: boolean;
    canBuy: boolean;
    actionText: string;
    actionIcon: string;
}

export class AppModuleRsp{
    id:string;
    name:string;
    price: number;
    expirationDate: string[];
    internalPrefixCode: string;
    active: boolean;
    partOfSubscription: boolean;
}

export class FeatModule extends AppModuleRsp{
    description: string;
    summary: string;
    purchased: boolean;
    icon: string;
    functionalities: FunctionEntry[];
    link: string;
    priceDescription: string;
    endDate:Date;
}

export class FunctionEntry {
    name: string;
    icon: string;
    link: string;
}

export class NameAndIcon {
    name: string;
    icon: string;
}

export enum ModuleAndResourceIDs {
    CORE = 'MODULE_1',
    MARKETING = 'MODULE_2',
    CALENDAR = 'MODULE_3',
    DOCUMENTARE_CLINICA = 'MODULE_4',
    DOCUMENTE = 'MODULE_5',
    RADIOGRAFIE_AFACERE = 'MODULE_6',
    TRASABILITATE = 'MODULE_7',
    MULTIMEDIA = 'MODULE_8',
    ABONAMENT = 'MODULE_9',
    RECALL = 'MODULE_A',
    FINANCIAL = 'MODULE_B',

    SMS = 'RESOURCE_'+ResourceType.SMS,
    MAIL = 'RESOURCE_'+ResourceType.EMAIL,
    AI_TOKENS = 'RESOURCE_'+ResourceType.AI_TOKENS,
    STORAGE = 'RESOURCE_'+ResourceType.STORAGE,
    WORK_UNIT = 'RESOURCE_'+ResourceType.WORK_UNIT,
}

export function getNmNdIco(rscID: ModuleAndResourceIDs|string): NameAndIcon {
    const map    = new Map<string, NameAndIcon>();
    map.set(ModuleAndResourceIDs.CORE, {name: 'Bază', icon: 'heroicons_solid:puzzle-piece'});
    map.set(ModuleAndResourceIDs.MARKETING, {name: 'Marketing', icon: 'heroicons_solid:megaphone'});
    map.set(ModuleAndResourceIDs.CALENDAR, {name: 'Calendar și programări', icon: 'heroicons_solid:calendar'});
    map.set(ModuleAndResourceIDs.DOCUMENTARE_CLINICA, {name: 'Documentare clinică stomatologie', icon: 'mat_solid:medical_services'});
    map.set(ModuleAndResourceIDs.DOCUMENTE, {name: 'Digitalizare documente și chestionare', icon: 'heroicons_solid:document-check'});
    map.set(ModuleAndResourceIDs.RADIOGRAFIE_AFACERE, {name: 'Radiografie Afacere', icon: 'heroicons_solid:chart-pie'});
    map.set(ModuleAndResourceIDs.TRASABILITATE, {name: 'Trasabilitatea sterilizării', icon: 'heroicons_solid:beaker'});
    map.set(ModuleAndResourceIDs.MULTIMEDIA, {name: 'Multimedia (starter 5GB)', icon: 'heroicons_solid:photo'});
    map.set(ModuleAndResourceIDs.ABONAMENT, {name: 'Abonament', icon: 'heroicons_solid:calendar-days'});
    map.set(ModuleAndResourceIDs.RECALL, {name: 'Recall', icon: 'heroicons_solid:chat-bubble-left-right'});
    map.set(ModuleAndResourceIDs.FINANCIAL, {name: 'Financiar', icon: 'heroicons_solid:banknotes'});

    map.set(ModuleAndResourceIDs.SMS, {name: 'Mesaj SMS', icon: 'heroicons_solid:chat-bubble-bottom-center-text'});
    map.set(ModuleAndResourceIDs.MAIL, {name: 'Mesaj Email', icon: 'heroicons_solid:envelope'});
    map.set(ModuleAndResourceIDs.AI_TOKENS, {name: 'AI Tokens', icon: 'heroicons_solid:cpu-chip'});
    map.set(ModuleAndResourceIDs.STORAGE, {name: 'Storage', icon: 'heroicons_solid:archive-box'});
    map.set(ModuleAndResourceIDs.WORK_UNIT, {name: 'Unit de lucru', icon: 'mat_outline:airline_seat_recline_extra'});
    return map.get(rscID);
}

export function getFeatureMap(): Map<string, FeatModule> {
    const map = new Map<string, FeatModule>();
    map.set('1', {
        id: '',
        name: getNmNdIco(ModuleAndResourceIDs.CORE).name,
        price: 0,
        expirationDate: [],
        internalPrefixCode: '1',
        active: true,
        partOfSubscription: false,
        summary: 'Accesezi gratuit funcționalitățile esențiale pentru gestionarea pacienților și organizarea clinicii.',
        description: `
            Beneficiezi de acces GRATUIT la funcționalitățile esențiale pentru gestionarea pacienților și a modulelor active din clinică.
            Platforma permite înregistrarea rapidă și eficientă, fără costuri inițiale.
            Ideal pentru o organizare fluidă și control complet asupra activității medicale. Începe simplu, evoluează inteligent.`,
        purchased:false,
        icon: getNmNdIco(ModuleAndResourceIDs.CORE).icon,
        functionalities: [
            {
                name: 'Management pacienți/clienți',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Management echipă și definire roluri',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Management clinică și servicii',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Achiziție resurse și administrare module',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
        ],
        link: 'https://www.smilesmanager.ro',
        priceDescription: '0 cost lunar',
        endDate: new Date(2025, 11, 20)
    });

    map.set('2', {
        id: '',
        name: getNmNdIco(ModuleAndResourceIDs.MARKETING).name,
        price: 10,
        expirationDate: [],
        internalPrefixCode: '2',
        active: false,
        partOfSubscription: false,
        // description: `Modulul de marketing permite crearea campaniilor SMS si Email de fidelizare a pacientilor care ruleaza automat bazat pe criterii definite.
        //               Cu ajutorul modulului puteti crea si gestiona campanii complexe de fidelizare, cum ar fi trimiterea de mesaje de ziua pacientilor, recall,
        //               sau reduceri targetate pe grupuri tinta.`,
        summary: 'Acest modul este in lucru si nu este inca disponibil pentru activare',
        description: `Acest modul este in lucru si nu este inca disponibil pentru activare`,
        purchased:false,
        icon: getNmNdIco(ModuleAndResourceIDs.MARKETING).icon,
        functionalities: [
            {
                name: 'Management audiente si filtre',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Management mesaje si sabloane',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Creaza, sterge, porneste si opreste campanii SMS/Email',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
        ],
        link: 'https://www.smilesmanager.ro',
        priceDescription: '0 cost lunar',
        endDate: new Date(2025, 11, 20)
    });

    map.set('3', {
        id: '',
        name: getNmNdIco(ModuleAndResourceIDs.CALENDAR).name,
        price: 10,
        expirationDate: [],
        internalPrefixCode: '3',
        active: true,
        partOfSubscription: true,
        summary: 'Sistemul inteligent de programări îți optimizează timpul prin notificări automate și sincronizare instantanee.',
        description: 'Optimizează-ți timpul cu un sistem inteligent de programări și calendar interactiv, creat pentru performanță. Beneficiezi de notificări automate, vizualizări flexibile și sincronizare instantanee între membrii echipei. Totul într-o platformă intuitivă, gata să transforme modul în care lucrezi. Eficiența devine rutina ta zilnică.',
        purchased:false,
        icon: getNmNdIco(ModuleAndResourceIDs.CALENDAR).icon,
        functionalities: [
            {
                name: 'Mesaje SMS/Email automate (reminder, confirmări și anulări)',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Front desk – gestionare programări',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Partajare și arbitrare unit dentar/medic)',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Personalizare setări calendar (culori, program de lucru, etc)',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Agendă detaliată zi curentă',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Acces fișă/trasabilitate/documente direct din calendar',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },

        ],
        link: 'https://www.smilesmanager.ro/calendar-programari-automate-sms-email-cabinet-stomatologic',
        priceDescription: 'cost / lună',
        endDate: new Date(2025, 11, 20)
    });

    map.set('4', {
        id: '',
        name: getNmNdIco(ModuleAndResourceIDs.DOCUMENTARE_CLINICA).name,
        price: 15,
        expirationDate: [],
        internalPrefixCode: '4',
        active: true,
        partOfSubscription: true,
        summary: 'Documentarea clinică interactivă îți permite să înregistrezi rapid tratamentele și să generezi planuri etapizate sincronizate cu restul fluxurilor.',
        description: `Descoperă modulul de documentare clinică interactivă, creat pentru o evidență clară și completă a statusului dentar.
         Înregistrezi rapid afecțiuni și tratamente, iar planul de tratament se generează etapizat, fără efort.
         Totul este sincronizat cu programările, notificările și trasabilitatea sterilizării.
         Asignează etape din plan către membrii echipei si ofera-i pacientului, în format fizic sau PDF un preventiv detaliat.`,
        purchased:false,
        icon: getNmNdIco(ModuleAndResourceIDs.DOCUMENTARE_CLINICA).icon,
        functionalities: [
            {
                name: 'Documentare clinică interactivă',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Plan de tratament etapizat interconectat',
                icon: 'heroicons_outline:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Status dentar grafic',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Distribuire plan tratament către colegi/discipline',
                icon: 'heroicons_outline:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Deviz pacient format fizic/PDF',
                icon: 'heroicons_outline:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Notițe și însemnări medici',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Avertizări alergii și condiții medicale',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Integrare modul trasabilitate sterilizare și calendar',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
        ],
        link: 'https://www.smilesmanager.ro',
        priceDescription: 'cost / lună',
        endDate: new Date(2025, 11, 20)
    });

    map.set('5', {
        id: '',
        name: getNmNdIco(ModuleAndResourceIDs.DOCUMENTE).name,
        price: 10,
        expirationDate: [],
        internalPrefixCode: '5',
        active: true,
        partOfSubscription: true,
        summary: 'Digitalizarea formularelor elimină aglomerația de la recepție și îmbunătățește prima impresie a pacientului.',
        description: `Într-un cabinet stomatologic modern, experiența pacientului începe încă din momentul în care trece pragul clinicii.
          Însă, cât de des această primă impresie este umbrită de teancurile de formulare de completat, de pixuri care nu funcționează sau de timp pierdut la recepție?
          Digitalizarea acestui proces aparent banal aduce beneficii majore, atât pentru pacienți, cât și pentru echipa medicală.<br><br>
          <span class="text-warn font-bold">ATENTIE!</span> Pentru o experiență completă se recomandă activarea modulului: <b>${getNmNdIco(ModuleAndResourceIDs.CALENDAR).name}</b>.`,
        purchased:false,
        icon: getNmNdIco(ModuleAndResourceIDs.DOCUMENTE).icon,
        functionalities: [
            {
                name: 'Digitalizare documente (GDPR, Consimțământ, Acord pacient informat...)',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Chestionare digitale',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Semnătură',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Flux de lucru – declanșare, completare, revizuire și finalizare document',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },

        ],
        link: 'https://www.smilesmanager.ro/digitalizare-formulare-medicale',
        priceDescription: 'cost / lună',
        endDate: new Date(2025, 11, 20)
    });

    map.set('6', {
        id: '',
        name: getNmNdIco(ModuleAndResourceIDs.RADIOGRAFIE_AFACERE).name,
        price: 10,
        expirationDate: [],
        internalPrefixCode: '6',
        active: true,
        partOfSubscription: false,
        summary: 'Analiza detaliată a afacerii îți evidențiază rapid punctele forte și vulnerabilitățile pentru decizii mai bune.',
        description: `La fel cum radiografia medicală oferă o imagine clară pentru diagnostic, analiza detaliată a afacerii tale îți <b>dezvăluie rapid punctele forte și vulnerabilitățile</b>.
                    Accesul la date relevante și vizualizarea lor prin grafice facilitează decizii informate.
                    Acest proces contribuie la <b>optimizarea costurilor și creșterea veniturilor</b>.
                    Imaginea de ansamblu devine astfel un instrument strategic în dezvoltarea afacerii.`,
        purchased:false,
        icon: getNmNdIco(ModuleAndResourceIDs.RADIOGRAFIE_AFACERE).icon,
        functionalities: [
            {
                name: 'Grafic venituri',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'KPI – RON/oră/clinică/medic',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'KPI – intervenții/oră/clinică/medic',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Comparație medici',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Rapoarte de recall',
                icon: 'heroicons_solid:wrench', link: 'https://www.smilesmanager.ro'
            },
        ],
        link: 'https://www.smilesmanager.ro',
        priceDescription: 'cost / lună',
        endDate: new Date(2025, 11, 20)
    });

    map.set('7', {
        id: '',
        name: getNmNdIco(ModuleAndResourceIDs.TRASABILITATE).name,
        price: 10,
        expirationDate: [],
        internalPrefixCode: '7',
        active: true,
        summary: 'Smiles Manager asigură trasabilitatea sterilizării prin registre digitale și etichete QR integrate în fluxul clinic.',
        description: `Conform Art. 53 din Ordinul nr. 1.761/2021, unitățile sanitare sunt obligate să asigure
                      trasabilitatea dispozitivelor medicale sterilizate prin înregistrări electronice sau letrice.
                      Smiles Manager facilitează respectarea acestei cerințe prin digitalizarea registrelor și generarea
                      de etichete cu cod QR. Etichetele pot fi asociate rapid la fișa pacientului prin scanare,
                      simplificând procesul. Integrarea cu modulul de documentare clinică și calendar este naturala oferind o soluție completă.`,
        purchased:false,
        partOfSubscription: true,
        icon: getNmNdIco(ModuleAndResourceIDs.TRASABILITATE).icon,
        functionalities: [
            {
                name: 'Registru sterilizări digital',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Definire OPIS (echipamente sterilizare, truse instrumente)',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Jurnal echipamente',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Grupare echipamente/cabinete/coduri',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Generare etichete sterilizare – cod QR',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Asociere fișă tratament – automat prin scanare',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Asociere fișă tratament – manual',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Integrare modul calendar',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Integrare modul documentare clinică',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },

        ],
        link: 'https://www.smilesmanager.ro/trasabilitate-sterilizare-instrumente-stomatologice',
        priceDescription: 'cost / lună',
        endDate: new Date(2025, 11, 20)
    });

    map.set('8', {
        id: '',
        name: getNmNdIco(ModuleAndResourceIDs.MULTIMEDIA).name,
        price: 20,
        expirationDate: [],
        internalPrefixCode: '8',
        active: true,
        summary: 'Modulul multimedia centralizează toate fișierele clinice ale pacientului într-un spațiu unic și ușor de folosit.',
        description: 'Modulul multimedia transformă fișa pacientului într-un hub complet de documentare clinică. Medicii pot încărca, organiza și vizualiza rapid toate tipurile de fișiere relevante pentru tratament, indiferent de sursa lor.',
        partOfSubscription: true,
        purchased:false,
        icon: getNmNdIco(ModuleAndResourceIDs.MULTIMEDIA).icon,
        functionalities: [
            {
                name: 'Stocare radiografii în fișa pacient',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Stocare imagini intraorale în fișa pacientului',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Încărcare imagini sau materiale video direct din camera smartphone‑ului personal',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Suport comparație înainte vs. după tratament',
                icon: 'heroicons_outline:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Stocare documente în fișa pacient',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Suport pentru funcționalitatea „drag and drop”',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Previzualizare și opțiuni de rotire și zoom pentru imagini',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
        ],
        link: 'https://www.smilesmanager.ro',
        priceDescription: 'cost / lună',
        endDate: new Date(2025, 11, 20)
    });


    map.set('9', {
        id: '',
        name: getNmNdIco(ModuleAndResourceIDs.ABONAMENT).name,
        price: 0,
        expirationDate: [],
        internalPrefixCode: '9',
        active: false,
        summary: 'Modelul pay‑as‑you‑use îți oferă control total asupra costurilor, plătind doar modulele și resursele utilizate.',
        description: 'Software stomatologic modern, dezvoltat special pentru clinici și cabinete dentare care își doresc control total asupra costurilor și funcționalităților. Modelul nostru de pricing este construit pe principiul pay-as-you-use: plătești doar modulele de care ai nevoie și resursele pe care le consumi efectiv, fără pachete rigide sau funcționalități inutile. Astfel, fiecare clinică dentară beneficiază de un sistem personalizat, adaptat exact volumului de lucru și nevoilor reale.',
        partOfSubscription: true,
        purchased:false,
        icon: getNmNdIco(ModuleAndResourceIDs.ABONAMENT).icon,
        functionalities: [
            {
                name: 'Abonament flexibil',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Activezi/Dezactivezi module oricând, costurile se ajustează instant, automat',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Cumperi resurse si le folosești când vrei, nu se pierde nimic',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
        ],
        link: 'https://www.smilesmanager.ro',
        priceDescription: 'cost / luna ',
        endDate: new Date(2025, 11, 20)
    });

    map.set('A', {
        id: '',
        name: getNmNdIco(ModuleAndResourceIDs.RECALL).name,
        price: 10,
        expirationDate: [],
        internalPrefixCode: 'A',
        active: true,
        summary: 'Modulul de Recall generează automat rechemări și trimite notificări personalizate pentru a crește numărul programărilor.',
        description: 'Modulul de Recall generează automat intrari de rechemare pe baza tipului de tratament sau a ultimei vizite, reducând timpul administrativ și creste numarul de programări, si implicit veniturile. Oferă trimitere integrată de SMS sau Email prin șabloane configurabile, cu posibilitatea de personalizare a mesajelor. Include sistem facil de notare feedback pentru monitorizarea eficientei recall-urilor pacienților și optimizarea serviciilor.',
        partOfSubscription: false,
        purchased:false,
        icon: getNmNdIco(ModuleAndResourceIDs.RECALL).icon,
        functionalities: [
            {
                name: 'Creare automata de recall bazat pe tip de tratament',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Configurare timp recall pt fiecare tratament individual',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Creare automata de recall bazat pe ultima vizita',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Mesaje SMS/Email de recall cu sablon personalizabil',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Suport simplu si facil pentru colectare feedback',
                icon: 'heroicons_outline:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Rapoarte de recall in modulul Radiografie afacere',
                icon: 'heroicons_solid:wrench', link: 'https://www.smilesmanager.ro'
            },
        ],
        link: 'https://www.smilesmanager.ro',
        priceDescription: 'cost / lună',
        endDate: new Date(2025, 11, 20)
    });

    map.set('B', {
        id: '',
        name: getNmNdIco(ModuleAndResourceIDs.FINANCIAL).name,
        price: 10,
        expirationDate: [],
        internalPrefixCode: 'B',
        active: true,
        summary: 'Modulul financiar face procesul de plată rapid, precis și complet transparent pentru pacient și echipă.',
        description: 'Modulul financiar transformă experiența de plată într-un proces fluid, profesionist și complet transparent — atât pentru pacient, cât și pentru echipa clinicii. Este construit pentru viteză, acuratețe și încredere, astfel încât fiecare interacțiune financiară să susțină imaginea premium a clinicii.',
        partOfSubscription: false,
        purchased:false,
        icon: getNmNdIco(ModuleAndResourceIDs.FINANCIAL).icon,
        functionalities: [
            {
                name: 'Creare nota de plata',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Gestionare discounts',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Plati partiale si suport avans',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Integrare cu module relevante (Documentare clinica, Calendar si programari, Radiografie afacere...)',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Balanta cont curent pacient',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Istoric tranzactii',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Integrare timp real fron desk <-> medic',
                icon: 'heroicons_solid:check', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Integrare procesator plata',
                icon: 'heroicons_solid:wrench', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Integrare solutii de plata mobile',
                icon: 'heroicons_solid:wrench', link: 'https://www.smilesmanager.ro'
            },
            {
                name: 'Integrare casa de marcat',
                icon: 'heroicons_solid:wrench', link: 'https://www.smilesmanager.ro'
            },
        ],
        link: 'https://www.smilesmanager.ro',
        priceDescription: 'cost / lună',
        endDate: new Date(2025, 11, 20)
    });

    return map;
}

export function getMoreInfoAboutResource(type:ResourceType) {
    switch(type) {
        case ResourceType.SMS:
            return {title:"Mesaje SMS", message:`
                <hr>
                <p>Mesajele SMS sunt un instrument esențial pentru comunicarea eficientă cu pacienții.</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Trimiteți reminder-uri automate pentru programări</li>
                    <li>Confirmați sau anulați programările</li>
                    <li>Transmiteți mesaje de marketing și promoții</li>
                    <li>Felicitări de ziua de naștere și sărbători</li>
                </ul>
                <p class="mt-2">Fiecare credit SMS permite trimiterea unui mesaj de până la 160 de caractere.</p><br>
<!--                <p class="mt-2"><span class="text-warn font-bold">Important:</span><br> Mesajele SMS expira după un an de zile. De fiecare data când achiziționați mesaje SMS, data de expirare se prelungește cu un an începând de la data achiziției.</p>-->
            `};
        case ResourceType.EMAIL:
            return {
                title: "Mesaje Email", message: `
                <hr>
                <p>Comunicarea prin email oferă posibilitatea de a transmite informații detaliate pacienților.</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Trimitere planuri de tratament</li>
                    <li>Newsletter-uri și campanii de marketing</li>
                    <li>Rapoarte și documente medicale</li>
                    <li>Confirmări programări cu detalii complete</li>
                </ul>
                <p class="mt-2">Fiecare credit Email permite trimiterea unui mesaj cu atașamente.</p><br>
                <p class="mt-2"><span class="text-warn font-bold">Important:</span><br> Mesajele Email expira după un an de zile. De fiecare data când achiziționați mesaje Email, data de expirare se prelungește cu un an începând de la data achiziției.</p>
            `
            };
        case ResourceType.AI_TOKENS:
            return {
                title: "AI Tokens", message: `
                <hr>
                <p>AI Tokens sunt utilizați pentru funcționalitățile bazate pe inteligență artificială.</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Analiza automată a radiografiilor</li>
                    <li>Generare de rapoarte și recomandări</li>
                    <li>Asistență în diagnosticare</li>
                    <li>Optimizarea fluxurilor de lucru</li>
                </ul>
                <p class="mt-2">Fiecare token reprezintă o unitate de procesare AI.</p>
            `
            };
        case ResourceType.STORAGE:
            return {
                title: "Stocare", message: `
                <hr>
                <p>Spațiul de stocare este esențial pentru păstrarea documentelor și fișierelor digitale.</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Stocare imagini radiologice</li>
                    <li>Arhivare documente medicale</li>
                    <li>Backup pentru fișe de pacient</li>
                    <li>Păstrare fotografii și videoclipuri</li>
                </ul>
                <p class="mt-2">Capacitatea este măsurată în GB, cu opțiuni de extindere după necesități.</p>
            `
            };
        case ResourceType.WORK_UNIT:
            return {
                title: "Unit de lucru", message: `
                <hr>
                <p>Unitatea de lucru reprezintă un scaun dentar sau stație de tratament.</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Gestionare programări per unitate</li>
                    <li>Monitorizare utilizare echipament</li>
                    <li>Planificare eficientă a resurselor</li>
                    <li>Raportare activitate per unitate</li>
                </ul>
                <p class="mt-2">Adăugați unități de lucru pentru a scala operațiunile clinicii.</p>
                <p class="mt-2"><span class="text-warn font-bold">Important:</span><br>Unitatea de lucru influențează numărul maxim de conturi pentru medici care pot fi create în platformă. Fiecare unitate de lucru permite accesul pentru 3 medici. Exemplu: Pentru a activa accesul pentru 5 medici în platformă, este necesară achiziționarea a minim 2 unități de lucru.</p>
            `
            };
    }
}


